import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom"
import { Group, Title, Grid, Loader, Divider, Button, Menu, Text, Badge, Space, Checkbox, TextInput, Center, Stack, Anchor } from "@mantine/core"
import { hideNotification, showNotification, updateNotification } from "@mantine/notifications";
import { modals } from '@mantine/modals';
import { IconCheck, IconChevronDown, IconChevronRight, IconFileX, IconX } from "@tabler/icons-react";
import _ from "lodash";
import { useProgramRegistrationCancelMutation, useProgramRegistrationDeleteMutation, useProgramRegistrationSaveMutation, useRegistrationByIdQuery } from "../../api/programregistration.api";
import { useProgramActivitiesQuery, useProgramActivitySessionsQuery, useProgramsQuery } from "../../api/program.api";
import { useUserPermissionQuery } from "../../api/user.api";
import { useTransactionByIdQuery } from "../../api/transaction.api";
import { formatDateRange, getParticipantsNames, getProgramRegistrationTotal } from "../../functions/programregistration.functions";
import DetailsView from "../../Components/ProgramRegistrations/DetailsView";
import ParticipantsView from "../../Components/ProgramRegistrations/ParticipantsView";
import SessionsView from "../../Components/ProgramRegistrations/SessionsView";
import ParentGuardiansView from "../../Components/ProgramRegistrations/ParentGuardiansView";
import { useProgramRegistrationState } from "../../state/programregistration.state";
import { RoleTemplate } from "../../Components/RoleTemplate"
import { ErrorBoundary } from "../../Components/Shared/ErrorBoundary";
import ErrorBoundaryFallback from "../../Components/Shared/ErrorBoundaryFallback";
import PaymentView from "../../Components/ProgramRegistrations/PaymentView";
import CustomFieldsView from "../../Components/ProgramRegistrations/CustomFieldsView";
import NotFound from "../../Components/ProgramRegistrations/NotFoundView";
import DiscountView from "../../Components/ProgramRegistrations/DiscountView";

export interface ViewRegistationPageParams {
	registrationId: string;
}

const ViewRegistationPage = () => {
	const { registrationId } = useParams<ViewRegistationPageParams>();
	const { data: registrationData, isLoading: isLoadingRegistration, isSuccess: programRegistrationLoaded, isError: programRegistrationNotLoaded } = useRegistrationByIdQuery(Number(registrationId));
	const { mutate: saveProgramRegistration, isLoading: isSaving, isError, error } = useProgramRegistrationSaveMutation();
	const { mutate: cancelProgramRegistration } = useProgramRegistrationCancelMutation();
	const { mutate: deleteProgramRegistration } = useProgramRegistrationDeleteMutation();
	const { programRegistration, updateProgramRegistration, setProgramRegistration, isChanged, isCancelled, isDeleted, status, setIsLoading, reset } = useProgramRegistrationState();
	const { data: transaction, isLoading: isLoadingTransaction } = useTransactionByIdQuery(programRegistration.TransactionId ?? 0);
	const { data: programs } = useProgramsQuery();
	const { data: programActivities } = useProgramActivitiesQuery();
	const { data: programActivitySessions } = useProgramActivitySessionsQuery();
	const { data: userPermissions } = useUserPermissionQuery();
	const useNewDesign = import.meta.env.VITE_NEW_DESIGN === "true";

	// const [programRegistrationDeleted, setProgramRegistrationDeleted] = useState(false);
	const participants = programRegistration.Data.Participants;
	let participantNames = participants.length > 0 ? `${participants[0].FirstName} ${participants[0].LastName}` : ''; //getParticipantsNames(programRegistration).join(', ');
	if (participants.length > 1) {
		participantNames += ` + ${participants.length - 1} other${participants.length > 2 ? 's' : ''}`;
	}
	const total = getProgramRegistrationTotal(programRegistration, programs ?? [], programActivities ?? [], programActivitySessions ?? []);
	const program = programs?.find(p => p.Id === (programRegistration?.Data.ProgramId ?? 0));

	const [sendEmailOnSave, setSendEmailOnSave] = useState(false);
	const [sendEmailAddress, setSendEmailAddress] = useState("");
	
	useEffect(() => {
		if (programRegistration.Id != Number(registrationId))
			reset();
	}, []);

	useEffect(() => {
		if (registrationData !== undefined && registrationData !== null) {
			setProgramRegistration(_.cloneDeep(registrationData), false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [programRegistrationLoaded])

	useEffect(() => {
		setIsLoading(isLoadingRegistration);
	}, [isLoadingRegistration])

	const canCancel = status.canCancel;
	const canDelete = status.canEdit && (userPermissions?.DeletePermission ?? false) && programRegistration.Cancelled == false && (transaction == null || transaction.Paid == undefined || transaction.Paid == 0) && programRegistration.Deleted == false;
	const refundRequired  = programRegistration.Total - programRegistration.Refunded > 0 && transaction != null && transaction.Paid > 0;

	const canSave = () => {

	}

	const onSave = () => { 
		const isNew = programRegistration.Id === 0;

		modals.openConfirmModal({
			title: 'Confirm Save',
			centered: true,
			children: <>
				<Text size="sm">
					Are you sure you want to save {!isNew && "the changes to"} this registration?
				</Text>
			</>,
			labels: { confirm: isNew ? 'Save' : 'Save Changes', cancel: 'Cancel' },
			confirmProps: { color: 'green' },
			// onCancel: () => console.log('Cancel'),
			onConfirm: () => performSave(),
		});

		// performSave();
	}

	const performSave = () => {
		showNotification({
			id: 'programregistration-save',
			loading: true,
			title: 'Saving your changes',
			message: 'Please wait while your changes are saved',
			autoClose: false,
			// disallowClose: true
		});
		
		saveProgramRegistration(programRegistration!, { 
			onSuccess: (response) => {
				setProgramRegistration(_.cloneDeep(response.data), false);
				updateNotification({
					id: 'programregistration-save',
					title: 'Success',
					message: 'Registration has been saved',
					color: 'green',
					icon: <IconCheck size={16} />,
					loading: false,
					autoClose: 3000
				});
			}, onError: (error) => {
				let errorMessage = (error as any)?.response?.data?.message;

				if (programRegistration.Data.Metadata.transaction) {
					programRegistration.Data.Metadata.transaction = undefined;
					updateProgramRegistration(programRegistration);
				}

				updateNotification({
					id: 'programregistration-save',
					title: 'Error',
					message: <>
						<Text mb="sm">An error occurred while trying to save the registration</Text>
						<Text fw="bold">{(error as any)?.response?.data?.message}</Text>
					</>,
					color: 'red',
					icon: <IconX size={16} />,
					loading: false,
					autoClose: 5000
				});
			}
		})
	}

	const onCancel = () => modals.openConfirmModal({
		title: 'Confirm cancellation',
		centered: true,
		children: <Stack gap="lg">
			<Text size="sm">
				Are you sure you want to cancel the registration for <strong>{participantNames}</strong>? This action cannot be undone.
			</Text>
			{refundRequired &&
			<Text size="sm" fw="bold" mb="md">
				Before you can cancel this registration you need to refund the entire payment.
			</Text>
			}
		</Stack>,
		labels: { confirm: 'Confirm Cancellation', cancel: 'Dismiss' },
		confirmProps: { color: 'red', disabled: refundRequired },
		// onCancel: () => console.log('Cancel'),
		onConfirm: () => performCancel(),
	});

	const performCancel = () => {
		showNotification({
			id: 'programregistration-cancel',
			loading: true,
			title: 'Cancelling registration',
			message: 'Please wait while the registration is cancelled',
			autoClose: false,
			// disallowClose: true
		});
		
		cancelProgramRegistration(programRegistration!, {
			onSuccess: () => {
				programRegistration.Cancelled = true;
				programRegistration.DateCancelled = new Date().toISOString();
				setProgramRegistration(programRegistration, false);
				// setProgramRegistrationDeleted(true);
				updateNotification({
					id: 'programregistration-cancel',
					title: 'Success',
					message: 'Registration has been cancelled',
					color: 'green',
					icon: <IconCheck size={16} />,
					loading: false,
					autoClose: 3000
				});
			}, onError: (error) => {
				updateNotification({
					id: 'programregistration-cancel',
					title: 'Error',
					message: 'An error occurred while trying to cancel the registration',
					color: 'red',
					icon: <IconX size={16} />,
					loading: false,
					autoClose: 3000
				});
			}
		})
	}

	const onDelete = () => modals.openConfirmModal({
		title: 'Confirm delete',
		centered: true,
		children: (
			<Text size="sm">
				Are you sure you want to delete the registration for <strong>{participantNames}</strong>? This action cannot be undone.
			</Text>
		),
		labels: { confirm: 'Confirm Delete', cancel: 'Cancel' },
		confirmProps: { color: 'red' },
		// onCancel: () => console.log('Cancel'),
		onConfirm: () => performDelete(),
	});

	const performDelete = () => {
		showNotification({
			id: 'programregistration-delete',
			loading: true,
			title: 'Deleting registration',
			message: 'Please wait while the registration is deleted',
			autoClose: false,
			// disallowClose: true
		});
		
		deleteProgramRegistration(programRegistration!, {
			onSuccess: () => {
				programRegistration.Deleted = true;
				setProgramRegistration(programRegistration, false);
				// setProgramRegistrationDeleted(true);
				updateNotification({
					id: 'programregistration-delete',
					title: 'Success',
					message: 'Registration has been deleted',
					color: 'green',
					icon: <IconCheck size={16} />,
					loading: false,
					autoClose: 3000
				});
			}, onError: (error) => {
				updateNotification({
					id: 'programregistration-delete',
					title: 'Error',
					message: 'An error occurred while trying to delete the registration',
					color: 'red',
					icon: <IconX size={16} />,
					loading: false,
					autoClose: 3000
				});
			}
		})
	}

	const onDiscard = () => {
		reset();
	}

	if (programRegistrationNotLoaded)
		return <NotFound />

	return <>
		<ErrorBoundary fallback={<ErrorBoundaryFallback />}>
		<RoleTemplate role="role.user.readonly" noAccess={<>You don't have access to this.</>}>
			<Group justify="space-between" align="start">
				<Stack gap={0}>
					<Group gap={5}>
						<Anchor component={Link} to="/" fz="sm" fw="bold">Home</Anchor>
						<IconChevronRight size="14" />
						<Anchor component={Link} to="/registrations" fz="sm" fw="bold">Registrations</Anchor>
						<IconChevronRight size="14" />
						<Text fw="bold" fz="sm">{program?.Title}</Text>
					</Group>
					<Group>
						<Title order={1}>{participantNames}</Title>
						{/* <InvoiceStatusBadge status={invoice.Status} /> */}
						{isCancelled && <Badge color="orange" variant="filled" size="sm" radius="sm">Cancelled</Badge>}
						{isDeleted && <Badge color="red" variant="filled" size="lg">Deleted</Badge>}
						{isLoadingRegistration && <Loader size="sm" ml="xs" pt={3} />}
					</Group>
					{/* <Text fw="bold" fz="lg">{program?.Title}: {formatDateRange(programRegistration?.Data?.DateFrom, programRegistration?.Data?.DateTo)}</Text> */}
					{/* <Text>Billed to <Anchor component={Link} to={`/customers/${invoice?.Customer.CustomerId}`} fw="bold">{invoice?.Customer.Name}</Anchor> · {currency.format(invoice?.Total ?? 0)}</Text> */}
				</Stack>
				
				<Group gap="xs" mb="xs" wrap="nowrap" style={{ alignSelf: 'flex-start' }}>
					{!isLoadingRegistration && !isChanged && !isDeleted && 
					<Menu position="bottom-end">
						<Menu.Target>
							<Button variant="filled" rightSection={<IconChevronDown />}>Actions</Button>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item onClick={onCancel} disabled={!canCancel} leftSection={<IconFileX />}>Cancel Registration</Menu.Item>
							<Menu.Divider />
							<Menu.Item onClick={onDelete} disabled={!canDelete} color="red" leftSection={<IconX />}>Delete Registration</Menu.Item>
						</Menu.Dropdown>
					</Menu>
					}
					{isChanged && <>
					<Button onClick={onSave} variant="filled" disabled={isSaving} hidden={!isChanged} leftSection={<IconCheck />}>Save Changes</Button>
					<Button onClick={onDiscard} variant="light" disabled={isSaving} hidden={!isChanged} leftSection={<IconX />}>Discard Changes</Button>
					</>}
				</Group>
			</Group>

			<Group justify="space-between">
				{/* <Title order={2} mb="sm">
					<Group gap="xs">
						<span>Registration for:</span>
						<Text inherit variant="gradient" style={{ lineHeight: "unset" }}>{participantNames}</Text>
						{isCancelled && <Badge color="orange" variant="filled" size="lg">Cancelled</Badge>}
						{isDeleted && <Badge color="red" variant="filled" size="lg">Deleted</Badge>}
						{isLoadingRegistration && <Loader size="sm" ml="xs" pt={3} />}
					</Group>
				</Title> */}

				{/* <Group gap="xs" mb="xs" wrap="nowrap" style={{ alignSelf: 'flex-start' }}>
					{!isLoadingRegistration && !isChanged && !isDeleted && 
					<Menu position="bottom-end">
						<Menu.Target>
							<Button variant="subtle" rightSection={<IconChevronDown />}>Actions</Button>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item onClick={onCancel} disabled={!canCancel} leftSection={<IconFileX />}>Cancel Registration</Menu.Item>
							<Menu.Divider />
							<Menu.Item onClick={onDelete} disabled={!canDelete} color="red" leftSection={<IconX />}>Delete Registration</Menu.Item>
						</Menu.Dropdown>
					</Menu>
					}
					{isChanged && <>
					<Button onClick={onSave} variant="filled" disabled={isSaving} hidden={!isChanged} leftSection={<IconCheck />}>Save Changes</Button>
					<Button onClick={onDiscard} variant="light" disabled={isSaving} hidden={!isChanged} leftSection={<IconX />}>Discard Changes</Button>
					</>}
				</Group> */}
			</Group>
			{/* <Divider /> */}

			{!programRegistrationLoaded && 
				<Center h={500} mx="auto">
					<Loader />
				</Center>
			}

			{programRegistrationLoaded &&			
				<Grid mt="lg" mb="md" gutter={useNewDesign ? 70 : 15}>
					{/* <Grid.Col span={{ xs: 12, sm: 6 }}>
						<ParticipantsView />
						<Space h={30} />
						<ParentGuardiansView />
					</Grid.Col>
					<Grid.Col span={{ xs: 12, sm: 6 }}>
						<DetailsView />
					</Grid.Col> */}
					<Grid.Col span={{ xs: 12, sm: 6 }}>
						<DetailsView />
						<ParentGuardiansView />
						<ParticipantsView />
					</Grid.Col>
					<Grid.Col span={{ xs: 12, sm: 6 }}>
						<SessionsView />
						<CustomFieldsView />
						<DiscountView />
						<PaymentView />
					</Grid.Col>
					{/* <Grid.Col xs={12} sm={6} lg={4}>
						<DetailsView />
					</Grid.Col>
					<Grid.Col xs={12} sm={6} lg={4}>
						<ParticipantsView />
					</Grid.Col>
					<Grid.Col xs={12} sm={6} lg={12} orderLg={3}>
						<SessionsView />
					</Grid.Col>
					<Grid.Col xs={12} sm={6} lg={4} orderLg={2}>
						<ParentGuardiansView />
					</Grid.Col> */}
				</Grid>
			}

		</RoleTemplate>
		<RoleTemplate role="role.admin">
			<small>
			<pre>
				{JSON.stringify(programRegistration, null, 2)}
			</pre>
			</small>
		</RoleTemplate>
		</ErrorBoundary>
	</>
}

export default ViewRegistationPage